import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { ChartPoint, ColumnProfile, formatValue } from '../../services/analytics';

interface AnalyticsChartProps {
  title: string;
  description: string;
  data: ChartPoint[];
  metric?: ColumnProfile;
  currency: string;
  selectedLabel?: string;
  onPointSelect?: (point: ChartPoint) => void;
}

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  title,
  description,
  data,
  metric,
  currency,
  selectedLabel,
  onPointSelect,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const maxValue = useMemo(() => Math.max(...data.map((point) => Math.abs(point.value)), 1), [data]);
  const isHorizontal = /category|numeric|top/i.test(title);
  const chartMinWidth = isHorizontal ? 430 : Math.max(420, data.length * 66);

  if (data.length === 0) {
    return (
      <div className="premium-card" style={{ padding: '1.5rem', minHeight: '260px' }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>{title}</h3>
        <p style={{ fontSize: '0.875rem', margin: 0 }}>GabMic needs matching date, category, or numeric columns to draw this chart.</p>
      </div>
    );
  }

  return (
    <div className="premium-card analytics-chart-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>{title}</h3>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>{description}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: 'auto' }}>
          {activeIndex !== null && data[activeIndex] && (
            <div style={{ textAlign: 'right', minWidth: '120px' }}>
              <span className="mono-label" style={{ marginBottom: '0.25rem' }}>{data[activeIndex].label}</span>
              <strong>{formatValue(data[activeIndex].value, metric, currency)}</strong>
            </div>
          )}
          <button
            className="icon-button"
            type="button"
            title={`Export ${title}`}
            aria-label={`Export ${title}`}
            onClick={() => exportChartAsSvg(title, data, metric, currency, isHorizontal)}
          >
            <Download size={16} />
          </button>
        </div>
      </div>

      {isHorizontal ? (
        <div className="chart-scroll-region" aria-label={`${title} scroll area`}>
          <div style={{ minWidth: chartMinWidth, display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {data.map((point, index) => {
              const width = `${Math.max(4, (Math.abs(point.value) / maxValue) * 100)}%`;
              const isActive = activeIndex === index;
              const isSelected = selectedLabel === point.label;

              return (
                <button
                  key={`${point.label}-${index}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  onBlur={() => setActiveIndex(null)}
                  onClick={() => onPointSelect?.(point)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(108px, 0.34fr) minmax(160px, 1fr) minmax(84px, auto)',
                    alignItems: 'center',
                    gap: '0.75rem',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--text-main)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '0.18rem',
                    borderRadius: '7px',
                    outline: isSelected ? '1px solid rgba(255,122,0,0.65)' : '1px solid transparent',
                  }}
                  aria-label={`${point.label}: ${formatValue(point.value, metric, currency)}`}
                >
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{point.label}</span>
                  <span style={{ height: '16px', borderRadius: '999px', background: 'rgba(255,255,255,0.055)', overflow: 'hidden' }}>
                    <span style={{
                      display: 'block',
                      width,
                      height: '100%',
                      borderRadius: '999px',
                      background: isActive || isSelected
                        ? 'linear-gradient(90deg, var(--gabmic-orange), #FFB266)'
                        : 'linear-gradient(90deg, #FF6A00, var(--gabmic-orange))',
                      transition: 'width 0.2s ease, background 0.2s ease',
                    }} />
                  </span>
                  <strong style={{ fontSize: '0.9rem', textAlign: 'right' }}>{formatValue(point.value, metric, currency)}</strong>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="chart-scroll-region" aria-label={`${title} scroll area`}>
          <div style={{ minWidth: chartMinWidth, display: 'grid', gridTemplateColumns: `repeat(${data.length}, minmax(46px, 1fr))`, gap: '0.75rem', alignItems: 'end', height: '200px' }}>
            {data.map((point, index) => {
              const height = `${Math.max(8, (Math.abs(point.value) / maxValue) * 100)}%`;
              const isActive = activeIndex === index;
              const isSelected = selectedLabel === point.label;

              return (
                <button
                  key={`${point.label}-${index}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  onBlur={() => setActiveIndex(null)}
                  onClick={() => onPointSelect?.(point)}
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    minWidth: 0,
                    outline: isSelected ? '1px solid rgba(255,122,0,0.65)' : '1px solid transparent',
                    borderRadius: '6px',
                  }}
                  aria-label={`${point.label}: ${formatValue(point.value, metric, currency)}`}
                >
                  <span
                    style={{
                      height,
                      width: '100%',
                      minHeight: '8px',
                      background: isActive || isSelected
                        ? 'linear-gradient(180deg, #FFB266, var(--gabmic-orange))'
                        : 'linear-gradient(180deg, rgba(255,122,0,0.9), rgba(255,122,0,0.28))',
                      borderRadius: '4px 4px 0 0',
                      boxShadow: isActive || isSelected ? '0 10px 26px rgba(255,122,0,0.18)' : 'none',
                      transition: 'background 0.2s, height 0.2s, box-shadow 0.2s',
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                    {point.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const exportChartAsSvg = (
  title: string,
  data: ChartPoint[],
  metric: ColumnProfile | undefined,
  currency: string,
  isHorizontal: boolean,
) => {
  const svg = isHorizontal
    ? buildHorizontalChartSvg(title, data, metric, currency)
    : buildVerticalChartSvg(title, data, metric, currency);
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${slugify(title)}-chart.svg`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const buildVerticalChartSvg = (title: string, data: ChartPoint[], metric: ColumnProfile | undefined, currency: string) => {
  const width = Math.max(760, data.length * 68);
  const height = 460;
  const chartTop = 86;
  const chartBottom = 370;
  const chartLeft = 54;
  const chartWidth = width - chartLeft - 34;
  const maxValue = Math.max(...data.map((point) => Math.abs(point.value)), 1);
  const barWidth = Math.max(26, chartWidth / data.length - 16);

  const bars = data.map((point, index) => {
    const slot = chartWidth / data.length;
    const barHeight = Math.max(8, (Math.abs(point.value) / maxValue) * (chartBottom - chartTop));
    const x = chartLeft + index * slot + (slot - barWidth) / 2;
    const y = chartBottom - barHeight;

    return `
      <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" rx="5" fill="url(#barGradient)" />
      <text x="${x + barWidth / 2}" y="${chartBottom + 28}" text-anchor="middle" fill="#B8B8B8" font-size="12">${escapeXml(shortenLabel(point.label, 10))}</text>
      <text x="${x + barWidth / 2}" y="${y - 8}" text-anchor="middle" fill="#F5F3EE" font-size="11">${escapeXml(formatValue(point.value, metric, currency))}</text>
    `;
  }).join('');

  return wrapChartSvg(title, width, height, `
    <line x1="${chartLeft}" y1="${chartBottom}" x2="${width - 28}" y2="${chartBottom}" stroke="#303030" />
    ${bars}
  `);
};

const buildHorizontalChartSvg = (title: string, data: ChartPoint[], metric: ColumnProfile | undefined, currency: string) => {
  const width = 860;
  const rowHeight = 52;
  const height = Math.max(280, 112 + data.length * rowHeight);
  const chartTop = 88;
  const labelWidth = 190;
  const barWidth = width - labelWidth - 110;
  const maxValue = Math.max(...data.map((point) => Math.abs(point.value)), 1);

  const rows = data.map((point, index) => {
    const y = chartTop + index * rowHeight;
    const widthPercent = Math.max(0.04, Math.abs(point.value) / maxValue);
    const fillWidth = barWidth * widthPercent;

    return `
      <text x="42" y="${y + 18}" fill="#D4D0C8" font-size="14">${escapeXml(shortenLabel(point.label, 22))}</text>
      <rect x="${labelWidth}" y="${y}" width="${barWidth}" height="18" rx="9" fill="#282828" />
      <rect x="${labelWidth}" y="${y}" width="${fillWidth}" height="18" rx="9" fill="url(#barGradient)" />
      <text x="${labelWidth + barWidth + 18}" y="${y + 15}" fill="#F5F3EE" font-size="13">${escapeXml(formatValue(point.value, metric, currency))}</text>
    `;
  }).join('');

  return wrapChartSvg(title, width, height, rows);
};

const wrapChartSvg = (title: string, width: number, height: number, body: string) => {
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <linearGradient id="barGradient" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#FF7A00" />
      <stop offset="100%" stop-color="#B64F00" />
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" rx="18" fill="#181818" />
  <rect x="0.5" y="0.5" width="${width - 1}" height="${height - 1}" rx="17.5" fill="none" stroke="#303030" />
  <text x="42" y="48" fill="#F5F3EE" font-size="24" font-weight="700">${escapeXml(title)}</text>
  <text x="42" y="70" fill="#9A9A9A" font-size="13">Exported from GabMic</text>
  ${body}
</svg>`;
};

const escapeXml = (value: string) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const shortenLabel = (value: string, length: number) => {
  return value.length > length ? `${value.slice(0, Math.max(0, length - 1))}...` : value;
};

const slugify = (value: string) => {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'gabmic';
};

export default AnalyticsChart;
